import { MAX_WIND, TERRAIN_STYLES, WORLDS, type TerrainStyle } from '@tanky/shared';
import { h, toast } from '../dom';
import type { TrainingSession } from '../game/training';

export interface TrainingPanelActions {
  /** Provede změnu relace (vrací false, když zrovna letí střela). */
  run(change: () => void, terrainChanged: boolean): boolean;
  setPlaceMode(on: boolean): void;
  placeMode(): boolean;
  /** Schová panel (znovu ho otevře ⚙ v horní liště). */
  hide(): void;
}

/** Malá obrazovka, kde panel zakrývá podstatnou část mapy. */
const smallScreen = () => matchMedia('(max-width: 700px), (max-height: 520px)').matches;

/** Plovoucí panel tréninku: vítr, cvičné cíle, mapa a statistika. */
export function trainingPanel(session: TrainingSession, actions: TrainingPanelActions) {
  const windVal = h('b', { class: 'wind-val' });
  const wind = h('input', { type: 'range', min: -MAX_WIND, max: MAX_WIND, step: 1, 'aria-label': 'Vítr' }) as HTMLInputElement;
  const placeBtn = h('button', { type: 'button', class: 'place-btn' });
  const stats = h('div', { class: 'train-stats' });
  const targetsInfo = h('span', { class: 'muted small' });

  const windText = (w: number) => (w === 0 ? 'bezvětří' : `${w < 0 ? '← ' : ''}${Math.abs(w)}${w > 0 ? ' →' : ''}`);

  function refresh() {
    const w = session.settings.wind;
    wind.value = String(w);
    // Na Měsíci není atmosféra – vítr nejde nastavit
    const calm = WORLDS[session.settings.style].wind === 0;
    wind.disabled = calm;
    windVal.textContent = calm ? 'bez atmosféry' : windText(w);
    const on = actions.placeMode();
    placeBtn.textContent = on ? '📍 Umisťuji – klepni na mapu' : '📍 Umisťovat cíle';
    placeBtn.classList.toggle('primary', on);
    const targets = session.state.tanks.filter((t) => t.target);
    const aliveCount = targets.filter((t) => t.alive).length;
    targetsInfo.textContent = `${aliveCount} / ${targets.length} na mapě`;
    const s = session.stats;
    stats.replaceChildren(
      h('span', {}, h('b', {}, String(s.shots)), ' výstřelů'),
      h('span', {}, h('b', {}, s.shots ? `${Math.round((s.hits / s.shots) * 100)} %` : '–'), ' zásahů'),
      h('span', {}, h('b', {}, String(s.damage)), ' škod'),
      h('span', {}, h('b', {}, String(s.best)), ' nejlepší'),
    );
    for (const b of styleBtns) b.classList.toggle('sel', b.dataset.style === session.settings.style);
  }

  wind.addEventListener('input', () => {
    windVal.textContent = windText(Number(wind.value));
  });
  wind.addEventListener('change', () => {
    if (!actions.run(() => session.setWind(Number(wind.value)), false)) refresh();
  });
  placeBtn.addEventListener('click', () => {
    const on = !actions.placeMode();
    actions.setPlaceMode(on);
    refresh();
    // Na mobilu panel zakrývá mapu – při umisťování ho rovnou schováme
    if (on && smallScreen()) actions.hide();
  });

  const btn = (label: string, fn: () => void, terrain: boolean, cls = '') =>
    h('button', { type: 'button', class: cls, onclick: () => actions.run(fn, terrain) && refresh() }, label);

  const styleBtns = TERRAIN_STYLES.map((st: TerrainStyle) =>
    h(
      'button',
      { type: 'button', 'data-style': st, onclick: () => actions.run(() => session.newMap(st), true) && refresh() },
      `${WORLDS[st].icon} ${WORLDS[st].name}`,
    ),
  );

  const el = h(
    'div',
    { class: 'train-panel' },
    h(
      'div',
      { class: 'train-row train-head' },
      h('span', { class: 'lbl' }, '⚙ Trénink'),
      h('button', { type: 'button', class: 'icon-btn', title: 'Schovat panel (otevřeš ho ⚙ nahoře)', onclick: () => actions.hide() }, '✕ Skrýt'),
    ),
    h('div', { class: 'train-row' }, h('span', { class: 'lbl' }, '🌬 Vítr'), windVal),
    h(
      'div',
      { class: 'train-row' },
      wind,
      btn('🎲', () => session.setWind(Math.round((Math.random() * 2 - 1) * MAX_WIND)), false, 'icon-btn'),
      btn('0', () => session.setWind(0), false, 'icon-btn'),
    ),
    h('div', { class: 'train-row' }, h('span', { class: 'lbl' }, '🎯 Cvičné cíle'), targetsInfo),
    placeBtn,
    h('div', { class: 'train-row' }, btn('♻ Oživit', () => session.reviveTargets(), false), btn('🗑 Smazat', () => session.clearTargets(), false)),
    h('div', { class: 'train-row' }, h('span', { class: 'lbl' }, '🗺 Mapa')),
    h('div', { class: 'train-row' }, ...styleBtns),
    h(
      'div',
      { class: 'train-row' },
      btn(
        '🔄 Nová mapa',
        () => {
          session.newMap(session.settings.style);
          toast('🔄 Byla vytvořena nová mapa.');
        },
        true,
      ),
      btn(
        '🧹 Obnovit terén',
        () => {
          session.resetTerrain();
          toast('🧹 Terén byl obnoven.');
        },
        true,
      ),
    ),
    h('div', { class: 'train-row' }, h('span', { class: 'lbl' }, '📊 Statistika')),
    stats,
  );

  refresh();
  return { el, refresh };
}
